<template>
    <div
        class="control select"
        :class="[`select--${appearance}`, { 'select--has-value': hasSelectedValue, 'select--searchable': searchable }]"
        :style="controlStyle"
    >
        <label
            v-if="label"
            class="control__label control-label"
        >
            {{ label }}
            <span
                v-if="required"
                class="control__required"
            >*</span>
        </label>

        <component
            :is="searchable ? VAutocomplete : VSelect"
            class="control__field"
            :autocomplete="searchable ? 'suppress' : undefined"
            :clearable="clearable"
            :disabled="disabled"
            :error-messages="errorMessages"
            :hide-details="hideDetails"
            :item-title="itemTitle"
            :item-value="itemValue"
            :items="items"
            :model-value="modelValue"
            :multiple="multiple"
            :name="name || undefined"
            :no-data-text="noDataText"
            :placeholder="placeholder"
            :readonly="readonly"
            :return-object="returnObject"
            :rules="validationRules"
            :density="density"
            menu-icon="mdi-chevron-down"
            rounded="lg"
            variant="outlined"
            @blur="$emit('blur', $event)"
            @focus="$emit('focus', $event)"
            @update:model-value="$emit('update:modelValue', $event)"
        >
            <template
                v-if="$slots.item"
                #item="itemSlotProps"
            >
                <slot
                    name="item"
                    v-bind="itemSlotProps"
                />
            </template>
            <template
                v-if="$slots.selection"
                #selection="selectionSlotProps"
            >
                <slot
                    name="selection"
                    v-bind="selectionSlotProps"
                />
            </template>
        </component>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { VAutocomplete, VSelect } from 'vuetify/components'

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object, Array],
    default: null,
  },

  label: {
    type: String,
    default: '',
  },

  placeholder: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: '',
  },

  noDataText: {
    type: String,
    default: undefined,
  },

  items: {
    type: Array,
    default: () => [],
  },

  itemTitle: {
    type: String,
    default: 'title',
  },

  itemValue: {
    type: String,
    default: 'value',
  },

  width: {
    type: [String, Number],
    default: '100%',
  },

  required: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  errorMessages: {
    type: [String, Array],
    default: '',
  },

  readonly: {
    type: Boolean,
    default: false,
  },

  clearable: {
    type: Boolean,
    default: false,
  },

  multiple: {
    type: Boolean,
    default: false,
  },

  returnObject: {
    type: Boolean,
    default: false,
  },

  searchable: {
    type: Boolean,
    default: false,
  },

  hideDetails: {
    type: [Boolean, String],
    default: 'auto',
  },

  appearance: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'filter'].includes(value),
  },

  density: {
    type: String,
    default: 'compact',
    validator: (value) => ['default', 'comfortable', 'compact'].includes(value),
  },
})

defineEmits(['update:modelValue', 'blur', 'focus'])

const controlStyle = computed(() => {
  const width = typeof props.width === 'number' ? `${props.width}px` : props.width

  return {
    '--form-control-width': width,
  }
})

const hasSelectedValue = computed(() => {
  if (Array.isArray(props.modelValue)) {
    return props.modelValue.length > 0
  }

  return props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== ''
})

const validationRules = computed(() => {
  if (!props.required) {
    return []
  }

  return [
    (value) => hasValue(value) || 'Obavezno polje',
  ]
})

function hasValue (value) {
  if (Array.isArray(value)) {
    return value.length > 0
  }

  return value !== null && value !== undefined && value !== ''
}
</script>

<style
    scoped
    lang="scss"
>
.select {
    --select-border-color: var(--color-control-border);
    --select-icon-color: var(--color-control-icon);
    --select-text-color: var(--color-text-body);
    --select-placeholder-color: var(--color-text-placeholder);
    --select-placeholder-font-size: var(--placeholder-font-size);
    --select-font-family: var(--font-family-base);
    --select-font-size: #{clamp-bp("sm", "xl", 15, 16)};
    --select-font-weight: 300;
    --select-text-align: left;
    --select-text-transform: none;

    &--filter {
        --v-disabled-opacity: 1;
        --v-medium-emphasis-opacity: 1;
        --select-border-color: var(--color-gray-3);
        --select-icon-color: var(--color-gray-3);
        --select-text-color: var(--color-gray-3);
        --select-placeholder-color: var(--color-gray-3);
        --select-placeholder-font-size: var(--select-font-size);
        --select-font-family: var(--font-family-control);
        --select-font-size: #{clamp-bp("sm", "xl", 13, 14)};
        --select-font-weight: 600;
        --select-text-align: center;
        --select-text-transform: uppercase;
    }

    :deep(.v-field) {
        background: var(--color-white);
    }

    &--filter {
        :deep(.v-field) {
            box-shadow: var(--shadow-control);
        }
    }

    :deep(.v-field__outline) {
        --v-field-border-width: 1px;
        --v-field-border-opacity: 1;
        color: var(--select-border-color);
    }

    :deep(.v-field__input) {
        align-items: center;
        padding-inline: 16px;
        color: var(--select-text-color);
        font-family: var(--select-font-family);
        font-size: 16px;
        font-weight: var(--select-font-weight);
        text-align: var(--select-text-align);
        text-transform: var(--select-text-transform);
    }

    :deep(.v-select__selection),
    :deep(.v-autocomplete__selection) {
        align-items: center;
        margin: 0;
    }

    &--filter {
        :deep(.v-field__input) {
            align-items: center;
            justify-content: center;
        }

        :deep(.v-field__field),
        :deep(.v-select__selection),
        :deep(.v-autocomplete__selection) {
            align-items: center;
            justify-content: center;
        }
    }

    :deep(.v-select__selection-text),
    :deep(.v-autocomplete__selection-text) {
        color: var(--select-text-color);
        font-family: var(--select-font-family);
        font-size: var(--select-font-size);
        font-weight: var(--select-font-weight);
        text-align: var(--select-text-align);
        text-transform: var(--select-text-transform);
    }

    :deep(input.v-field__input) {
        color: transparent;
        font-family: var(--select-font-family);
        font-size: var(--select-font-size);
        font-weight: var(--select-font-weight);
        text-align: var(--select-text-align);
        text-transform: var(--select-text-transform);
    }

    &--has-value {
        :deep(input.v-field__input) {
            opacity: 0;
        }
    }

    &--searchable {
        :deep(input.v-field__input) {
            color: var(--select-text-color);
        }

        &.select--has-value {
            :deep(input.v-field__input) {
                opacity: 1;
            }
        }
    }

    :deep(input.v-field__input::placeholder),
    :deep(.v-field__input input::placeholder) {
        color: var(--select-placeholder-color);
        -webkit-text-fill-color: var(--select-placeholder-color);
        font-family: var(--select-font-family);
        font-size: var(--select-placeholder-font-size);
        font-weight: var(--select-font-weight);
        text-align: var(--select-text-align);
        text-transform: var(--select-text-transform);
        opacity: 1;
    }

    :deep(.v-field__append-inner) {
        align-items: center;
        padding-top: 0;
        padding-right: 16px;
    }

    :deep(.v-icon) {
        color: var(--select-icon-color);
        opacity: 1;
    }
}
</style>
